import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Box, Typography, Button, Alert, Snackbar, IconButton } from '@mui/material'
import { Warning as WarningIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import { logout, loginSuccess } from '../store/slices/authSlice'
import { useRefreshTokenMutation, useLogoutMutation } from '../store/api/authApi'
import { sessionApi } from '../services/api/sessionApi'
import { resolveSessionTimeoutMinutes } from '@utils/authRememberMe'
import { SessionWarning } from './SessionWarning'

interface SessionManagerState {
  timeRemaining: number
  isWarningActive: boolean
  isExpired: boolean
  lastActivity: number
}

interface SessionManagerProps {
  warningThreshold?: number // Minutes before timeout to show warning (default: 5)
  sessionTimeout?: number // Session timeout in minutes (default: 30)
  checkInterval?: number // How often to check session in seconds (default: 120)
  onSessionExpired?: () => void
  onSessionExtended?: () => void
  children?: React.ReactNode
}

const MAX_BACKOFF_MS = 10 * 60 * 1000

/** Auth-session snackbars: fill the viewport on xs; never a desktop-only minWidth. */
// eslint-disable-next-line react-refresh/only-export-components -- shared sx is asserted in SessionManager tests
export const SESSION_MANAGER_ALERT_SX = {
  width: '100%',
  maxWidth: { xs: '100%', sm: 480 },
} as const

/**
 * SessionManager Component
 * 
 * Provides client-side session management including:
 * - Automatic session timeout detection
 * - Warning notifications before expiration
 * - One-click session extension
 * - Activity tracking for timeout prevention
 * - Graceful handling of session expiration
 */
export const SessionManager: React.FC<SessionManagerProps> = ({
  warningThreshold = 5,
  sessionTimeout = 30,
  checkInterval = 120,
  onSessionExpired,
  onSessionExtended,
  children
}) => {
  const { user, rememberMe } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  const [refreshTokenMutation] = useRefreshTokenMutation()
  const [logoutMutation] = useLogoutMutation()
  const effectiveSessionTimeout = resolveSessionTimeoutMinutes(rememberMe, sessionTimeout)
  const [sessionState, setSessionState] = useState<SessionManagerState>({
    timeRemaining: effectiveSessionTimeout * 60, // Convert to seconds
    isWarningActive: false,
    isExpired: false,
    lastActivity: Date.now()
  })

  const [showWarning, setShowWarning] = useState(false)
  const [showExpired, setShowExpired] = useState(false)
  const [isExtending, setIsExtending] = useState(false)
  const [showWarningDialog, setShowWarningDialog] = useState(false)
  
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const consecutiveFailuresRef = useRef(0)
  const currentIntervalRef = useRef(checkInterval * 1000)

  // Track user activity to prevent session timeout
  const trackActivity = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      lastActivity: Date.now()
    }))
  }, [])

  // Calculate time remaining until session timeout
  const calculateTimeRemaining = useCallback(() => {
    const now = Date.now()
    const sessionAge = (now - sessionState.lastActivity) / 1000 // Convert to seconds
    const timeoutSeconds = effectiveSessionTimeout * 60
    const remaining = Math.max(0, timeoutSeconds - sessionAge)
    
    return remaining
  }, [sessionState.lastActivity, effectiveSessionTimeout])

  // Schedule the next session check using the current (possibly backed-off) interval.
  const scheduleNextCheck = useCallback((nextIntervalMs: number, checkFn: () => void) => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current)
    }
    currentIntervalRef.current = nextIntervalMs
    intervalRef.current = setTimeout(checkFn, nextIntervalMs)
  }, [])

  // Handle session expiration
  const handleSessionExpired = useCallback(async () => {
    setSessionState(prev => ({
      ...prev,
      isExpired: true,
      isWarningActive: false
    }))

    setShowWarning(false)
    setShowWarningDialog(false)
    setShowExpired(true)

    // Call logout mutations and dispatch
    await logoutMutation()
    dispatch(logout())

    if (onSessionExpired) {
      onSessionExpired()
    }
  }, [dispatch, logoutMutation, onSessionExpired])

  // Check session status and update UI
  const checkSessionStatus = useCallback(async () => {
    if (!user) return

    try {
      const sessionValid = await sessionApi.validateSession()

      if (!sessionValid) {
        if (rememberMe) {
          try {
            const refreshed = await refreshTokenMutation().unwrap()
            dispatch(loginSuccess({
              user,
              token: refreshed.accessToken,
              rememberMe: true,
            }))
            consecutiveFailuresRef.current = 0
            scheduleNextCheck(checkInterval * 1000, checkSessionStatus)
            return
          } catch {
            handleSessionExpired()
            return
          }
        }
        handleSessionExpired()
        return
      }

      // Success — reset failure count and return to normal interval.
      consecutiveFailuresRef.current = 0
      scheduleNextCheck(checkInterval * 1000, checkSessionStatus)

      const timeRemaining = calculateTimeRemaining()
      const warningThresholdSeconds = warningThreshold * 60

      setSessionState(prev => ({
        ...prev,
        timeRemaining
      }))

      // Show inactivity warning only for standard (non-remember-me) sessions.
      if (!rememberMe && timeRemaining <= warningThresholdSeconds && timeRemaining > 0) {
        if (!sessionState.isWarningActive) {
          setSessionState(prev => ({ ...prev, isWarningActive: true }))
          setShowWarning(true)
          setShowWarningDialog(true)

          // Auto-hide snackbar after 30 seconds (dialog stays open)
          warningTimeoutRef.current = setTimeout(() => {
            setShowWarning(false)
          }, 30000)
        }
      }

      // Handle session expiration (inactivity). Remember-me uses a 30-day window.
      if (!rememberMe && timeRemaining === 0) {
        handleSessionExpired()
      }

    } catch (error) {
      // Don't expire session on transient errors — only on explicit invalidation.
      // Apply exponential backoff: 2x the normal interval on first failure,
      // doubling each subsequent failure, capped at MAX_BACKOFF_MS (~10 minutes).
      consecutiveFailuresRef.current += 1
      const backoffMs = Math.min(
        checkInterval * 1000 * Math.pow(2, consecutiveFailuresRef.current),
        MAX_BACKOFF_MS
      )
      // Only log on the first failure to avoid flooding the console when the
      // backend is persistently unavailable.
      if (consecutiveFailuresRef.current === 1) {
        console.warn(
          `Session check failed, applying backoff (next retry in ${Math.round(backoffMs / 1000)}s):`,
          error
        )
      }
      scheduleNextCheck(backoffMs, checkSessionStatus)
    }
  }, [user, rememberMe, dispatch, refreshTokenMutation, calculateTimeRemaining, warningThreshold, sessionState.isWarningActive, checkInterval, scheduleNextCheck, handleSessionExpired])

  // Extend session
  const extendSession = useCallback(async () => {
    if (!user || isExtending) return

    setIsExtending(true)

    try {
      const refreshed = await refreshTokenMutation().unwrap()
      dispatch(loginSuccess({
        user,
        token: refreshed.accessToken,
        rememberMe,
      }))
      
      setSessionState(prev => ({
        ...prev,
        lastActivity: Date.now(),
        isWarningActive: false,
        timeRemaining: effectiveSessionTimeout * 60
      }))
      
      setShowWarning(false)
      setShowWarningDialog(false)
      onSessionExtended?.()
      
    } catch (error) {
      console.error('Failed to extend session:', error)
      // If refresh fails, session is truly expired
      handleSessionExpired()
    } finally {
      setIsExtending(false)
    }
  }, [user, isExtending, rememberMe, dispatch, refreshTokenMutation, effectiveSessionTimeout, onSessionExtended, handleSessionExpired])

  // Format time remaining for display
  const formatTimeRemaining = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}${remainingSeconds > 0 ? ` ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`
    }
    
    return `${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`
  }, [])

  // Set up activity tracking
  useEffect(() => {
    if (!user) return

    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'
    ]

    const handleActivity = () => {
      trackActivity()
      
      // Clear existing timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current)
      }
      
      // Set new timeout to stop tracking after period of inactivity
      activityTimeoutRef.current = setTimeout(() => {
        // User stopped being active
      }, 60000) // 1 minute of no activity
    }

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current)
      }
    }
  }, [user, trackActivity])

  // Set up session checking — uses dynamic setTimeout scheduling so the interval
  // can be extended via exponential backoff when the backend is unavailable.
  useEffect(() => {
    if (!user) return

    // Reset backoff state and start with the normal interval.
    consecutiveFailuresRef.current = 0
    currentIntervalRef.current = checkInterval * 1000

    // Initial check runs immediately; subsequent checks are self-scheduled
    // inside checkSessionStatus (success → normal interval, failure → backoff).
    checkSessionStatus()

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current)
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
    }
  }, [user, checkSessionStatus, checkInterval])

  // Don't render anything if no user
  if (!user) {
    return <>{children}</>
  }

  return (
    <>
      {children}
      
      {/* Session Warning Dialog */}
      <SessionWarning
        open={showWarningDialog}
        timeRemaining={Math.floor(sessionState.timeRemaining)}
        sessionTimeout={effectiveSessionTimeout}
        onExtend={extendSession}
        onLogout={handleSessionExpired}
        isExtending={isExtending}
      />
      
      {/* Session Warning Snackbar (backup notification) */}
      <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {formatTimeRemaining(Math.floor(sessionState.timeRemaining))} remaining
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={extendSession}
                disabled={isExtending}
                startIcon={<RefreshIcon />}
              >
                {isExtending ? 'Extending...' : 'Stay Logged In'}
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setShowWarning(false)}
              >
                ×
              </IconButton>
            </Box>
          }
          sx={SESSION_MANAGER_ALERT_SX}
        >
          <Typography variant="body2">
            Your session will expire soon due to inactivity.
          </Typography>
        </Alert>
      </Snackbar>

      {/* Session Expired Dialog */}
      <Snackbar
        open={showExpired}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              variant="contained"
              onClick={async () => {
                await logoutMutation()
                dispatch(logout())
              }}
            >
              Log In Again
            </Button>
          }
          sx={SESSION_MANAGER_ALERT_SX}
        >
          <Typography variant="body2">
            Your session has expired. You will be redirected to the login page.
          </Typography>
        </Alert>
      </Snackbar>
    </>
  )
}
